import { useEffect, useState } from "react";

import PageHeader from "../components/PageHeader.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import apiClient from "../services/api.js";

const defaultProfile = {
  address: "",
  city: "",
  state: "",
  occupation: "",
  employer: "",
  next_of_kin: "",
  next_of_kin_phone: "",
  bank_name: "",
  account_number: ""
};

export default function OnboardingPage() {
  const { updateMember } = useAuth();
  const [profile, setProfile] = useState(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await apiClient.get("/members/me");
        if (!active) return;
        updateMember(data);
        setProfile({ ...defaultProfile, ...(data.profile_data || {}) });
      } catch (err) {
        if (!active) return;
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [updateMember]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("");
    setError("");
    try {
      setSaving(true);
      const { data } = await apiClient.put("/members/me", {
        profile_data: profile
      });
      updateMember(data);
      setStatus("Onboarding details saved.");
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to save details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title="Member onboarding"
        description="Capture cooperative profile details for investment and compliance workflows."
      />
      <form onSubmit={handleSubmit} className="card-surface rounded-3xl p-6 space-y-4">
        {loading ? (
          <p className="text-sm text-slate-500">Loading profile...</p>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Address
                </label>
                <input
                  name="address"
                  value={profile.address}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  City
                </label>
                <input
                  name="city"
                  value={profile.city}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  State
                </label>
                <input
                  name="state"
                  value={profile.state}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Occupation
                </label>
                <input
                  name="occupation"
                  value={profile.occupation}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Employer
                </label>
                <input
                  name="employer"
                  value={profile.employer}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Next of kin
                </label>
                <input
                  name="next_of_kin"
                  value={profile.next_of_kin}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Next of kin phone
                </label>
                <input
                  name="next_of_kin_phone"
                  value={profile.next_of_kin_phone}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Bank name
                </label>
                <input
                  name="bank_name"
                  value={profile.bank_name}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                  Account number
                </label>
                <input
                  name="account_number"
                  value={profile.account_number}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm"
                />
              </div>
            </div>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            {status && <p className="text-sm text-emerald-600">{status}</p>}
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-accent px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save onboarding"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
